import css from './imageInput.module.scss';

import { memo, useEffect, useState } from 'react';

import { apiGetResource } from 'core/api';
import { getBlobUrlFromB64Url } from 'core/helpers/browser';
import { useStableCallback } from 'core/hooks/useStableCallback';
import { TBinaryUpdate } from 'shared/types/domain';

import { useMessageModal } from '../MessageModal';
import ImageResource from './ImageResource';
import { useOnPaste } from './useOnPaste';

type Props = {
  name: string;
  label?: string;
  resourceUid: string | null | undefined;
  resourceBinary: TBinaryUpdate | null | undefined;
  onChange: (event: { name: string; value: string }) => void;
};

function ImageInput({
  label = 'Image',
  name,
  resourceBinary,
  resourceUid,
  onChange,
}: Props) {
  const [url, setUrl] = useState<string | null>(null);
  const { showError } = useMessageModal();

  const update = useStableCallback((update: TBinaryUpdate) => {
    onChange({
      name,
      value: JSON.stringify(update),
    });
  });

  useEffect(() => {
    // When it's set it overrides `resourceUid`.
    if (resourceBinary) {
      if (resourceBinary.action === 'remove') {
        setUrl(null);
      } else {
        setUrl(getBlobUrlFromB64Url(resourceBinary.data));
      }

      return;
    }

    if (resourceUid) {
      apiGetResource(resourceUid).then(setUrl).catch(showError);
    }
  }, [resourceUid, resourceBinary]);

  const onPaste = useOnPaste(update);

  return (
    <div className={css.root}>
      {url ? (
        <ImageResource label={label} url={url} onChange={update} />
      ) : (
        <input
          className={css.input}
          placeholder={`${label}. Put a focus and paste an image`}
          // Don't react on changes, but allow to put a focus.
          onChange={() => null}
          value=""
          onPaste={evt => void onPaste(evt)}
        />
      )}
    </div>
  );
}

export default memo(ImageInput);
